/** Autolien compose : URL tapée / collée → `<a href>` cliquable. */

const AUTOLINK_URL_SOURCE = String.raw`(?:https?:\/\/|www\.)[^\s<>"'\\\x60]+`;
const SKIP_AUTOLINK_TAGS = new Set(["A", "CODE", "PRE", "SCRIPT", "STYLE", "TEXTAREA", "NOSCRIPT"]);

function createAutolinkUrlRe(): RegExp {
  return new RegExp(AUTOLINK_URL_SOURCE, "gi");
}

function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Retire la ponctuation de fin de phrase collée à l’URL (`voir https://a.fr.`). */
export function trimUrlTrailingPunctuation(raw: string): string {
  let value = raw.replace(/[.,;:!?]+$/u, "");
  const opens = (value.match(/\(/g) ?? []).length;
  const closes = (value.match(/\)/g) ?? []).length;
  let extra = Math.max(0, closes - opens);
  while (extra > 0 && value.endsWith(")")) {
    value = value.slice(0, -1);
    extra -= 1;
  }
  return value;
}

export function normalizeAutolinkHref(raw: string): string | null {
  const trimmed = trimUrlTrailingPunctuation(raw.trim());
  if (!trimmed) return null;

  let href = trimmed;
  if (/^www\./i.test(href)) href = `https://${href}`;
  if (!/^https?:\/\//i.test(href)) return null;

  try {
    const url = new URL(href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    const host = url.hostname.replace(/\.$/, "");
    if (!host.includes(".")) return null;
    const tld = host.split(".").pop() ?? "";
    if (!/^[a-z]{2,}$/i.test(tld)) return null;
    return href;
  } catch {
    return null;
  }
}

export function isAutolinkSkipNode(node: Node | null): boolean {
  let el = node instanceof Element ? node : node?.parentElement ?? null;
  while (el) {
    if (SKIP_AUTOLINK_TAGS.has(el.tagName)) return true;
    if (el.getAttribute("data-mail-quote") === "1") return true;
    if (el.getAttribute("data-mail-signature") === "1") return true;
    el = el.parentElement;
  }
  return false;
}

function createAutolinkAnchor(doc: Document, href: string, text: string): HTMLAnchorElement {
  const anchor = doc.createElement("a");
  anchor.href = href;
  anchor.rel = "noopener noreferrer";
  anchor.target = "_blank";
  anchor.textContent = text;
  return anchor;
}

function buildAnchorHtml(href: string, text: string): string {
  return `<a href="${escapeHtmlText(href)}" rel="noopener noreferrer" target="_blank">${escapeHtmlText(text)}</a>`;
}

export function isStandaloneAutolinkText(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  const re = createAutolinkUrlRe();
  const match = re.exec(trimmed);
  if (!match || match.index !== 0) return false;
  const cleaned = trimUrlTrailingPunctuation(match[0]);
  if (!normalizeAutolinkHref(cleaned)) return false;
  return trimmed.slice(match[0].length).trim() === "";
}

export function linkifyPlainTextToHtml(text: string): { html: string; changed: boolean } {
  const re = createAutolinkUrlRe();
  const parts: string[] = [];
  let last = 0;
  let changed = false;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const raw = match[0];
    const cleaned = trimUrlTrailingPunctuation(raw);
    const href = normalizeAutolinkHref(cleaned);
    if (!href) continue;
    parts.push(escapeHtmlText(text.slice(last, match.index)));
    parts.push(buildAnchorHtml(href, cleaned));
    parts.push(escapeHtmlText(raw.slice(cleaned.length)));
    last = match.index + raw.length;
    changed = true;
  }

  parts.push(escapeHtmlText(text.slice(last)));
  return {
    html: parts.join("").replace(/\r\n|\r|\n/g, "<br>"),
    changed,
  };
}

function replaceTextNodeWithAutolinks(doc: Document, textNode: Text): boolean {
  const text = textNode.textContent ?? "";
  const re = createAutolinkUrlRe();
  const frag = doc.createDocumentFragment();
  let last = 0;
  let changed = false;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const raw = match[0];
    const cleaned = trimUrlTrailingPunctuation(raw);
    const href = normalizeAutolinkHref(cleaned);
    if (!href) continue;
    if (match.index > last) {
      frag.appendChild(doc.createTextNode(text.slice(last, match.index)));
    }
    frag.appendChild(createAutolinkAnchor(doc, href, cleaned));
    const punct = raw.slice(cleaned.length);
    if (punct) frag.appendChild(doc.createTextNode(punct));
    last = match.index + raw.length;
    changed = true;
  }

  if (!changed) return false;
  if (last < text.length) {
    frag.appendChild(doc.createTextNode(text.slice(last)));
  }
  textNode.replaceWith(frag);
  return true;
}

/** Lienifie les URL en texte brut sous un nœud (hors citation / signature / `<a>`). */
export function linkifyPlainUrlsInRoot(root: Element): boolean {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (isAutolinkSkipNode(node)) continue;
    const text = node.textContent ?? "";
    if (!createAutolinkUrlRe().test(text)) continue;
    nodes.push(node as Text);
  }

  let changed = false;
  for (const textNode of nodes) {
    if (replaceTextNodeWithAutolinks(doc, textNode)) changed = true;
  }
  return changed;
}

/** Lienifie les URL en texte brut dans un fragment HTML (hors citation / signature / `<a>`). */
export function linkifyPlainUrlsInHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed || typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(`<div id="mail-autolink-root">${html}</div>`, "text/html");
  const root = doc.getElementById("mail-autolink-root");
  if (!root) return html;
  const changed = linkifyPlainUrlsInRoot(root);
  return changed ? root.innerHTML : html;
}

/** Enveloppe l’URL juste avant le curseur (Espace / Entrée), sans réécrire tout l’éditeur. */
export function autolinkUrlBeforeCaret(editor: HTMLElement | null): boolean {
  if (!editor) return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;

  const node = sel.anchorNode;
  if (!node || node.nodeType !== Node.TEXT_NODE || !editor.contains(node)) return false;
  if (isAutolinkSkipNode(node)) return false;

  const offset = sel.anchorOffset;
  const text = node.textContent ?? "";
  const before = text.slice(0, offset);
  const re = new RegExp(`(?:${AUTOLINK_URL_SOURCE})$`, "i");
  const match = before.match(re);
  if (!match?.[0]) return false;

  const cleaned = trimUrlTrailingPunctuation(match[0]);
  const href = normalizeAutolinkHref(cleaned);
  if (!href) return false;

  const start = offset - match[0].length;
  const range = document.createRange();
  range.setStart(node, start);
  range.setEnd(node, start + cleaned.length);

  const anchor = createAutolinkAnchor(document, href, cleaned);
  range.deleteContents();
  range.insertNode(anchor);

  const leftover = match[0].length - cleaned.length;
  const after = document.createRange();
  const next = anchor.nextSibling;
  if (leftover > 0 && next?.nodeType === Node.TEXT_NODE) {
    after.setStart(next, Math.min(leftover, next.textContent?.length ?? leftover));
  } else {
    after.setStartAfter(anchor);
  }
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);
  return true;
}

export function insertAutolinkedPlainText(text: string): boolean {
  const { html, changed } = linkifyPlainTextToHtml(text);
  if (!changed) return false;

  if (document.execCommand("insertHTML", false, html)) return true;

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const frag = document.createDocumentFragment();
  while (tmp.firstChild) frag.appendChild(tmp.firstChild);
  range.insertNode(frag);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}
